import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { motion } from "framer-motion";
import { FiArrowLeft, FiExternalLink } from "react-icons/fi";
import PageWrapper from "../components/ui/PageWrapper";
import { articles } from "../data/articles";

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <PageWrapper className="pt-28 pb-20 bg-terminal">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-2xl font-bold text-brightText mb-4">
            Article not found
          </h1>
          <Link
            to="/articles"
            className="text-accentBlue hover:text-neonBlue transition-colors"
          >
            Back to articles
          </Link>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="pt-28 pb-20 bg-terminal">
      <div className="max-w-3xl mx-auto px-6">
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-sm text-dimText hover:text-accentBlue transition-colors mb-8"
        >
          <FiArrowLeft size={16} /> Back to articles
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm text-accentBlue font-mono font-medium mb-2">
            {article.date}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-brightText mb-4">
            {article.title}
          </h1>
          {article.sourceUrl && (
            <a
              href={article.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-dimText hover:text-accentBlue transition-colors mb-8"
            >
              View original <FiExternalLink size={12} />
            </a>
          )}
        </motion.div>

        <motion.article
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="article-content prose prose-xl prose-invert max-w-none prose-headings:text-brightText prose-a:text-accentBlue prose-p:text-dimText prose-p:text-xl prose-p:leading-relaxed prose-strong:text-brightText prose-img:rounded-xl prose-img:border prose-img:border-terminalBorder prose-blockquote:border-accentBlue/40 prose-li:text-dimText prose-li:text-xl"
        >
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              h2: ({ node, children, ...props }) => (
                <motion.h2
                  variants={sectionVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  {...props}
                >
                  {children}
                </motion.h2>
              ),
              h3: ({ node, children, ...props }) => (
                <motion.h3
                  variants={sectionVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  {...props}
                >
                  {children}
                </motion.h3>
              ),
              p: ({ node, children, ...props }) => {
                const hasDisplayMath = node.children?.some(
                  (child) =>
                    child.type === "element" &&
                    (child.properties?.className?.includes("math-display") ||
                     child.properties?.className?.includes("katex-display"))
                );
                const Tag = hasDisplayMath ? motion.div : motion.p;
                return (
                  <Tag
                    variants={sectionVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-40px" }}
                    {...props}
                    className={hasDisplayMath ? "my-8 text-center" : props.className}
                  >
                    {children}
                  </Tag>
                );
              },
              blockquote: ({ node, children, ...props }) => (
                <motion.blockquote
                  variants={sectionVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  {...props}
                >
                  {children}
                </motion.blockquote>
              ),
              img: ({ node, ...props }) => (
                <motion.img
                  variants={sectionVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  loading="lazy"
                  className="rounded-xl border border-terminalBorder w-full h-auto my-6"
                  {...props}
                />
              ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </motion.article>
      </div>
    </PageWrapper>
  );
}
